import { cva, cx, type VariantProps } from 'class-variance-authority'
import { AlertCircle, Info } from 'lucide-react'
import { forwardRef, ReactNode } from 'react'
import Icon from './Icon'
import Paragraph from './Paragraph'
import Tooltip from './Tooltip'

const inputStyles = cva(
    [
        'w-full h-[32px] text-sm bg-white border-0.5 rounded-2',
        'focus:outline-none focus:ring-[3px] focus:ring-opacity-100',
        'transition-shadow duration-150 ease-in-out',
        'placeholder:text-textPlaceholder',
        'disabled:text-textDisabled disabled:bg-backgroundDisabled'
    ],
    {
        variants: {
            state: {
                default: ['border-borderPrimary', 'focus:border-borderTertiary focus:ring-borderEmphasis'],
                complete: ['border-borderPrimary', 'focus:border-borderTertiary focus:ring-borderEmphasis'],
                error: [
                    'text-textNegative',
                    'border-borderNegativeSubdued',
                    'focus:border-borderNegativeSubdued focus:ring-borderEmphasisNegative'
                ]
            },
            iconPosition: {
                left: 'pl-9 pr-3',
                right: 'pr-9 pl-3',
                none: 'px-3'
            }
        },
        defaultVariants: {
            state: 'default',
            iconPosition: 'none'
        }
    }
)

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement>, VariantProps<typeof inputStyles> {
    label?: string
    required?: boolean
    leftIcon?: React.ReactNode
    rightIcon?: React.ReactNode
    message?: string
    tooltip?: ReactNode
    'data-testid'?: string
    onRightIconClick?: () => void
    onLeftIconClick?: () => void
}

const Input = forwardRef<HTMLInputElement, InputProps>(
    (
        {
            className,
            label,
            required,
            leftIcon,
            rightIcon,
            message,
            tooltip,
            onRightIconClick,
            onLeftIconClick,
            state = 'default',
            'data-testid': dataTestId,
            ...props
        },
        ref
    ) => {
        const iconPosition = leftIcon ? 'left' : rightIcon ? 'right' : 'none'

        return (
            <div className="w-full" data-testid="__input-wrapper__">
                {label && (
                    <label htmlFor={props.name} className="flex text-sm font-medium mb-2" data-testid="__input-label__">
                        {label}
                        {required && (
                            <span className="text-textNegative ml-1" data-testid="__input-required-indicator__">
                                *
                            </span>
                        )}
                        {tooltip && (
                            <Tooltip
                                content={tooltip}
                                trigger={
                                    <span>
                                        <Icon icon={Info} size="1" className="ml-1" />
                                    </span>
                                }
                            />
                        )}
                    </label>
                )}
                <div className="relative">
                    {leftIcon && (
                        <button
                            type="button"
                            onClick={onLeftIconClick}
                            className="absolute inset-y-0 left-0 pl-3 flex items-center"
                            tabIndex={0}
                            data-testid="__left-icon-button__"
                        >
                            {leftIcon}
                        </button>
                    )}
                    <input
                        id={props.name}
                        className={cx(inputStyles({ state, iconPosition }), className)}
                        ref={ref}
                        data-testid={dataTestId ?? '__input-field__'}
                        {...props}
                    />
                    {(rightIcon || state === 'error') && (
                        <button
                            type="button"
                            onClick={onRightIconClick}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                            tabIndex={0}
                            data-testid="__right-icon-button__"
                        >
                            {state === 'error' ? (
                                <span data-testid="__error-icon__" className="w-[16px] h-[16px]">
                                    <Icon icon={AlertCircle} size="1" color="negative" />
                                </span>
                            ) : (
                                rightIcon
                            )}
                        </button>
                    )}
                </div>
                {message && (
                    <Paragraph
                        className="pt-2"
                        size={3}
                        color={state === 'error' ? 'negative' : undefined}
                        data-testid="__input-message__"
                    >
                        {message}
                    </Paragraph>
                )}
            </div>
        )
    }
)

export default Input
